import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";

const HomePage = lazy(() => import("./pages/vendas/HomePage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminChats = lazy(() => import("./pages/admin/AdminChats"));
const ClaraPage = lazy(() => import("./pages/clara/ClaraPage"));
const Clara1Page = lazy(() => import("./pages/clara/Clara1Page"));
const MetodoLivrePage = lazy(() => import("./pages/vendas/MetodoLivrePage"));
const OAppPage = lazy(() => import("./pages/vendas/OAppPage"));
const ManifestoPage = lazy(() => import("./pages/vendas/ManifestoPage"));
const QuemSomosPage = lazy(() => import("./pages/vendas/QuemSomosPage"));
const AjudaPage = lazy(() => import("./pages/vendas/AjudaPage"));
const BlogPage = lazy(() => import("./pages/vendas/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/vendas/BlogPostPage"));
const SegurancaPage = lazy(() => import("./pages/vendas/SegurancaPage"));
const TermosPage = lazy(() => import("./pages/vendas/TermosPage"));
const PrivacidadePage = lazy(() => import("./pages/vendas/PrivacidadePage"));
const ParceirosLivrePage = lazy(() => import("./pages/vendas/ParceirosLivrePage"));

import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div
      role="status"
      aria-live="polite"
      aria-label="Carregando página"
      className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent"
    />
  </div>
);

const CookieConsentGate = () => {
  const { pathname } = useLocation();
  // Não mostrar o banner de cookies na página da Clara.
  if (pathname.toLowerCase().startsWith("/clara")) return null;
  return <CookieConsent />;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LazyMotion features={domAnimation} strict>
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/chats" element={<AdminChats />} />
                <Route path="/admin/chats/:sessionId" element={<AdminChats />} />
                <Route path="/" element={<HomePage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/metodo-livre" element={<MetodoLivrePage />} />
                <Route path="/o-app" element={<OAppPage />} />
                <Route path="/manifesto" element={<ManifestoPage />} />
                <Route path="/quem-somos" element={<QuemSomosPage />} />
                <Route path="/ajuda" element={<AjudaPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/seguranca" element={<SegurancaPage />} />
                <Route path="/termos" element={<TermosPage />} />
                <Route path="/termos-de-uso" element={<TermosPage />} />
                <Route path="/privacidade" element={<PrivacidadePage />} />
                <Route path="/politica-de-privacidade" element={<PrivacidadePage />} />
                <Route path="/parceiros-livre" element={<ParceirosLivrePage />} />

                <Route path="/clara" element={<ClaraPage />} />
                <Route path="/clara1" element={<Clara1Page />} />
                <Route path="*" element={<HomePage />} />
              </Routes>
            </Suspense>
            <CookieConsentGate />
          </BrowserRouter>
        </LazyMotion>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
